import { useState } from "react"
import { Link as RouterLink } from "react-router-dom"
import { Alert, Button, Link, Stack, TextField, Typography } from "@mui/material"
import { authApi } from "../api"
import AuthLayout from "./AuthLayout"

/**
 * Ask for a reset link.
 *
 * ── Username, not email ─────────────────────────────────────────────────────
 *
 * One mailbox routinely covers a whole team here, with an account each — which
 * is why signing in is by username. An address therefore does not identify an
 * account, and this screen used to accept one anyway: the server sent a link to
 * every account behind it, each naming its username.
 *
 * That was correct and it was still wrong. Somebody who wanted their own
 * password reset got four emails, three of which reset a colleague's, and the
 * shared mailbox was left holding three live tokens nobody had asked for.
 *
 * So it asks for the username. Forgotten that too? That is a conversation with
 * an administrator, not a credential left in a shared inbox.
 *
 * ── Why the confirmation is shown for nothing as well ───────────────────────
 *
 * The success state appears for every input, including usernames with no
 * account — matching the server, which answers identically on purpose. Saying
 * "no such account" here would hand back the membership oracle the endpoint is
 * built to withhold.
 *
 * The one thing that does change the answer is a deployment with no mail
 * configured, because that is a fact about the server and not about any
 * account. Promising a link that no server can send is how somebody loses an
 * afternoon to a spam folder.
 */
export default function ForgotPassword() {
  const [username, setUsername] = useState("")
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  const submit = async (event) => {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      setResult(await authApi.forgotPassword(username.trim()))
    } catch (err) {
      // Only a malformed request or a rate limit reaches here; a missing account
      // does not.
      setError(err.userMessage || "Could not send the reset link")
    } finally {
      setBusy(false)
    }
  }

  const backToSignIn = (
    <Link component={RouterLink} to="/login" variant="body2" underline="hover">
      Back to sign in
    </Link>
  )

  if (result && result.email_configured === false) {
    return (
      <AuthLayout title="Email is not set up here" subtitle="Financials platform" footer={backToSignIn}>
        <Stack spacing={2}>
          <Alert severity="warning">{result.message}</Alert>
          <Typography variant="body2" color="text.secondary">
            Nothing was sent, and nothing about your account has changed. An administrator can set a password for you
            from the Users screen.
          </Typography>
          <Button variant="outlined" onClick={() => setResult(null)} fullWidth>
            Back
          </Button>
        </Stack>
      </AuthLayout>
    )
  }

  if (result) {
    return (
      <AuthLayout title="Check your email" subtitle="Financials platform" footer={backToSignIn}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            If <strong>{username.trim()}</strong> is an account, a reset link is on its way to the address on it. Links
            are good for a short time — request another if one expires.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Nothing arrived? Check the spam folder. If you are not sure which username is yours, ask an administrator
            rather than guessing — a link only ever reaches the one account.
          </Typography>
          <Button variant="outlined" onClick={() => setResult(null)} fullWidth>
            Try a different username
          </Button>
        </Stack>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Reset your password" subtitle="Financials platform" footer={backToSignIn}>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <form onSubmit={submit}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Enter your username and we will send a link to the address on your account.
          </Typography>
          <TextField
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoFocus
            required
            fullWidth
            helperText="The name you sign in with — not your email, which several accounts may share."
          />
          <Button type="submit" variant="contained" size="large" disabled={busy} fullWidth>
            {busy ? "Sending…" : "Send reset link"}
          </Button>
        </Stack>
      </form>
    </AuthLayout>
  )
}
